import React, { useState, useEffect } from 'react';
import './JsonViewer.scss'; // ← новый CSS
import { getAPIUrl } from "../../api/apiUtils";
import { apiFetch } from "../../api/apiClient";

const API_BASE_URL = getAPIUrl();

const JsonViewer = ({ data, title = "JSON Data" }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [groups, setGroups] = useState({});
    const [showTooltip, setShowTooltip] = useState({ id: null, x: 0, y: 0 });

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const response = await apiFetch(`${API_BASE_URL}/monitoring/groups`);
                if (response.ok) {
                    const groupsData = await response.json();
                    const groupsMap = {};
                    groupsData.forEach(group => {
                        groupsMap[group.group_id] = group.group_name;
                    });
                    setGroups(groupsMap);
                }
            } catch (error) {
                console.error('Ошибка при загрузке групп:', error);
            }
        };
        fetchGroups();
    }, []);

    useEffect(() => {
        const handleScroll = () => setShowTooltip({ id: null, x: 0, y: 0 });
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, []);

    if (!data) {
        return (
            <div className="json-viewer-root">
                <div className="json-viewer-header">
                    <div className="json-viewer-title-group">
                        <h3 className="json-viewer-title">{title}</h3>
                    </div>
                </div>
                <div className="json-viewer-content expanded">
                    <div className="json-viewer-json" style={{ color: '#64748b', fontStyle: 'italic' }}>
                        No data available
                    </div>
                </div>
            </div>
        );
    }

    const formatJson = (jsonData) => {
        try {
            const parsed = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
            return JSON.stringify(parsed, null, 2);
        } catch {
            return jsonData;
        }
    };

    const copyToClipboard = async () => {
        try {
            const formattedData = formatJson(data);
            await navigator.clipboard.writeText(formattedData);
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }
    };

    const handleMouseEnter = (event, groupId) => {
        const container = event.currentTarget.closest('.json-viewer-root');
        const containerRect = container.getBoundingClientRect();
        const targetRect = event.currentTarget.getBoundingClientRect();
        const scrollTop = container.scrollTop || 0;
        const scrollLeft = container.scrollLeft || 0;
        setShowTooltip({
            id: groupId,
            x: targetRect.left - containerRect.left + targetRect.width / 2 + scrollLeft,
            y: targetRect.top - containerRect.top - 10 + scrollTop
        });
    };

    const handleMouseLeave = () => {
        setShowTooltip({ id: null, x: 0, y: 0 });
    };

    const renderValueWithTooltip = (value, key) => {
        if ((key === 'community_id' || key === 'group_id') && groups[value]) {
            return (
                <span
                    className="json-string json-string-tooltip"
                    onMouseEnter={(e) => handleMouseEnter(e, value)}
                    onMouseLeave={handleMouseLeave}
                >
                    "{value}"
                </span>
            );
        }
        return <span className="json-string">"{value}"</span>;
    };

    const renderJsonValue = (value, depth = 0, key = null) => {
        const indent = '  '.repeat(depth);
        
        if (value === null) return <span className="json-null">null</span>;
        if (typeof value === 'boolean') return <span className="json-boolean">{value.toString()}</span>;
        if (typeof value === 'number') return <span className="json-number">{value}</span>;
        if (typeof value === 'string') return renderValueWithTooltip(value, key);
        if (Array.isArray(value)) {
            if (value.length === 0) return <span className="json-bracket">[]</span>;
            return (
                <div style={{ marginLeft: '1rem' }}>
                    <span className="json-bracket">[</span>
                    {value.map((item, index) => (
                        <div key={index} style={{ marginLeft: '1rem' }}>
                            {renderJsonValue(item, depth + 1, index.toString())}
                            {index < value.length - 1 && <span className="json-bracket">,</span>}
                        </div>
                    ))}
                    <span className="json-bracket">]</span>
                </div>
            );
        }
        if (typeof value === 'object') {
            const keys = Object.keys(value);
            if (keys.length === 0) return <span className="json-bracket">{'{}'}</span>;
            return (
                <div style={{ marginLeft: '1rem' }}>
                    <span className="json-bracket">{'{'}</span>
                    {keys.map((k, index) => (
                        <div key={k} style={{ marginLeft: '1rem' }}>
                            <span className="json-key">"{k}"</span>
                            <span className="json-bracket">: </span>
                            {renderJsonValue(value[k], depth + 1, k)}
                            {index < keys.length - 1 && <span className="json-bracket">,</span>}
                        </div>
                    ))}
                    <span className="json-bracket">{'}'}</span>
                </div>
            );
        }
        return <span style={{ color: '#4b5563' }}>{String(value)}</span>;
    };

    const renderFormattedJson = () => {
        try {
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            return renderJsonValue(parsed);
        } catch {
            return (
                <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: '0.375rem', border: '1px solid #e2e8f0' }}>
                    <pre className="json-viewer-json">{data}</pre>
                </div>
            );
        }
    };

    return (
        <div className="json-viewer-root">
            {/* Header */}
            <div className="json-viewer-header">
                <div className="json-viewer-title-group">
                    <i className="fas fa-code json-viewer-icon"></i>
                    <h3 className="json-viewer-title">{title}</h3>
                    <span className="json-viewer-badge">
                        {typeof data === 'string' ? 'JSON' : 'Object'}
                    </span>
                </div>
                <div className="json-viewer-actions">
                    <button
                        onClick={copyToClipboard}
                        className={`json-viewer-btn ${isCopied ? 'copied' : ''}`}
                        title="Copy to clipboard"
                    >
                        {isCopied ? (
                            <i className="fas fa-check"></i>
                        ) : (
                            <i className="fas fa-copy"></i>
                        )}
                    </button>
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="json-viewer-btn"
                        title={isExpanded ? "Collapse" : "Expand"}
                    >
                        <i className={`fas fa-${isExpanded ? 'compress' : 'expand'}`}></i>
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className={`json-viewer-content ${isExpanded ? 'expanded' : 'collapsed'}`}>
                <div className="json-viewer-json">
                    {renderFormattedJson()}
                </div>
            </div>

            {/* Footer */}
            <div className="json-viewer-footer">
                <div className="json-footer-info">
                    <div className="json-footer-item">
                        <i className="fas fa-info-circle"></i>
                        <span>{typeof data === 'string' ? `${data.length} characters` : 'Object data'}</span>
                    </div>
                    <div className="json-footer-item">
                        <i className="fas fa-clock"></i>
                        <span>{new Date().toLocaleTimeString()}</span>
                    </div>
                </div>
                <div>
                    <span className="json-footer-badge">
                        {isExpanded ? 'Expanded' : 'Collapsed'}
                    </span>
                </div>
            </div>

            {/* Tooltip */}
            {showTooltip.id && (
                <div
                    className="json-tooltip"
                    style={{
                        left: showTooltip.x,
                        top: showTooltip.y,
                        transform: 'translateX(-50%) translateY(-100%)'
                    }}
                >
                    <div className="json-tooltip-content">
                        <i className="fas fa-users json-tooltip-icon"></i>
                        {groups[showTooltip.id]}
                    </div>
                    <div className="json-tooltip-arrow"></div>
                </div>
            )}
        </div>
    );
};

export default JsonViewer;